import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowLeft, AlertTriangle, X, Loader2, Users, CheckCircle2 } from "lucide-react";

const fmt = (v, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c || "EUR" }).format(v || 0);
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("pt-PT"); } catch { return d; }
};

const PAYMENT_LABEL = {
  MB: "Multibanco", MW: "MBWay", PP: "PayPal",
  CT: "Cartão", CP: "Cofidis", TB: "Transf. Bancária",
};

/**
 * Página CRM: encomendas em AP (Aguardar Pagamento) da rede do consultor.
 * Permite cancelar encomendas presas (ex: abandono no checkout MBWay).
 * Migrada de `/my/rede/encomendas-pendentes` para o CRM.
 */
export default function DownlinePendingOrdersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [cancelling, setCancelling] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [okMsg, setOkMsg] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/encomendas/rede/pendentes");
      setRows(r?.data?.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleCancel = async (orderId) => {
    setCancelling(orderId);
    setErr(""); setOkMsg("");
    try {
      const r = await api.post(`/encomendas/rede/${orderId}/cancelar`);
      const msg = r?.data?.message || `Encomenda #${orderId} cancelada com sucesso.`;
      setOkMsg(msg);
      toast.success(msg);
      setConfirmId(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Erro ao cancelar";
      setErr(detail);
      toast.error(detail);
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div data-testid="crm-downline-orders-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Link to="/encomendas" className="inline-flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#0F1829] mb-4">
        <ArrowLeft size={14} /> Voltar a Encomendas
      </Link>

      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users size={20} className="text-[#0F1829]" />
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#111827]"
                style={{ fontFamily: "'Fraunces', sans-serif" }}
                data-testid="downline-orders-title">
              Encomendas pendentes da minha rede
            </h1>
          </div>
          <p className="text-sm text-[#6B7280]">
            Encomendas em <strong>Aguardar Pagamento</strong> de clientes da sua estrutura — últimos 6 meses.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="downline-orders-refresh"
          className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-md bg-white text-[#0F1829] hover:bg-[#F9FAFB]"
        >
          Actualizar
        </button>
      </div>

      {okMsg && (
        <div className="mb-4 p-3 bg-[#ECFDF5] border border-[#A7F3D0] rounded-md text-sm text-[#065F46] flex items-start gap-2" data-testid="downline-orders-success">
          <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
          <span className="flex-1">{okMsg}</span>
          <button onClick={() => setOkMsg("")} className="text-[#065F46] hover:text-[#064E3B]"><X size={14} /></button>
        </div>
      )}

      {err && (
        <div className="mb-4 p-3 bg-[#FEF2F2] border border-[#FECACA] rounded-md text-sm text-[#991B1B]" data-testid="downline-orders-error">
          {err}
        </div>
      )}

      {loading ? (
        <div className="p-8 bg-white border border-[#E5E7EB] rounded-md text-sm text-center text-[#6B7280]">
          <Loader2 size={16} className="inline mr-2 animate-spin" /> A carregar encomendas da sua rede…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 bg-white border border-[#E5E7EB] rounded-md text-sm text-center text-[#6B7280]" data-testid="downline-orders-empty">
          Nenhuma encomenda pendente na sua rede.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
          {/* Mobile: cards */}
          <ul className="md:hidden divide-y divide-[#E5E7EB]" data-testid="downline-orders-mobile-list">
            {rows.map((o) => (
              <li key={o.ID_Encomenda} data-testid={`downline-order-card-${o.ID_Encomenda}`} className="p-3">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-[#6B7280]">
                      <Link to={`/encomendas/${o.ID_Encomenda}`}
                        data-testid={`downline-order-link-mobile-${o.ID_Encomenda}`}
                        className="hover:text-[#0F1829] hover:underline">#{o.ID_Encomenda}</Link>
                    </div>
                    <div className="font-medium text-[#0F1829] truncate">{o.NomeCompleto}</div>
                    <div className="text-[11px] text-[#6B7280] truncate">{o.Email || "—"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-[#0F1829]">{fmt(o.PrecoFinal, o.ID_Moeda)}</div>
                    <div className="text-[11px] text-[#6B7280]">{fmtDate(o.DataEncomenda)}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="px-2 py-0.5 bg-[#FFFBEB] border border-[#FCD34D] text-[#92400E] text-xs rounded">
                    {PAYMENT_LABEL[o.ID_TipoPagamento] || o.ID_TipoPagamento}
                  </span>
                  {confirmId === o.ID_Encomenda ? (
                    <span className="inline-flex items-center gap-1.5">
                      <button
                        data-testid={`downline-confirm-cancel-${o.ID_Encomenda}-mobile`}
                        onClick={() => handleCancel(o.ID_Encomenda)}
                        disabled={cancelling === o.ID_Encomenda}
                        className="px-2 py-1 text-xs bg-[#991B1B] text-white rounded hover:bg-[#7F1D1D] disabled:opacity-60"
                      >
                        {cancelling === o.ID_Encomenda ? "A cancelar…" : "Confirmar"}
                      </button>
                      <button
                        onClick={() => setConfirmId(null)}
                        disabled={cancelling === o.ID_Encomenda}
                        className="px-2 py-1 text-xs bg-white border border-[#E5E7EB] rounded text-[#6B7280]"
                      >
                        Não
                      </button>
                    </span>
                  ) : (
                    <button
                      data-testid={`downline-cancel-btn-${o.ID_Encomenda}-mobile`}
                      onClick={() => setConfirmId(o.ID_Encomenda)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#FCA5A5] text-[#991B1B] rounded hover:bg-[#FEF2F2]"
                    >
                      <X size={12} /> Cancelar
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {/* Desktop: table */}
          <table className="hidden md:table w-full text-sm">
            <thead className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2.5 text-left font-medium">Encomenda</th>
                <th className="px-3 py-2.5 text-left font-medium">Cliente</th>
                <th className="px-3 py-2.5 text-right font-medium">Total</th>
                <th className="px-3 py-2.5 text-left font-medium">Pagamento</th>
                <th className="px-3 py-2.5 text-left font-medium">Data</th>
                <th className="px-3 py-2.5 text-right font-medium">Acção</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {rows.map((o) => (
                <tr key={o.ID_Encomenda} data-testid={`downline-order-row-${o.ID_Encomenda}`} className="hover:bg-[#F9FAFB]">
                  <td className="px-3 py-2.5 font-mono text-[#0F1829]">
                    <Link to={`/encomendas/${o.ID_Encomenda}`}
                      data-testid={`downline-order-link-${o.ID_Encomenda}`}
                      className="hover:underline">#{o.ID_Encomenda}</Link>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-[#0F1829]">{o.NomeCompleto}</div>
                    <div className="text-[11px] text-[#6B7280]">{o.Email || "—"}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold text-[#0F1829]">{fmt(o.PrecoFinal, o.ID_Moeda)}</td>
                  <td className="px-3 py-2.5">
                    <span className="px-2 py-0.5 bg-[#FFFBEB] border border-[#FCD34D] text-[#92400E] text-xs rounded">
                      {PAYMENT_LABEL[o.ID_TipoPagamento] || o.ID_TipoPagamento}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[#374151]">{fmtDate(o.DataEncomenda)}</td>
                  <td className="px-3 py-2.5 text-right">
                    {confirmId === o.ID_Encomenda ? (
                      <span className="inline-flex items-center gap-1.5">
                        <button
                          data-testid={`downline-confirm-cancel-${o.ID_Encomenda}`}
                          onClick={() => handleCancel(o.ID_Encomenda)}
                          disabled={cancelling === o.ID_Encomenda}
                          className="px-2 py-1 text-xs bg-[#991B1B] text-white rounded hover:bg-[#7F1D1D] disabled:opacity-60"
                        >
                          {cancelling === o.ID_Encomenda ? "A cancelar…" : "Confirmar"}
                        </button>
                        <button
                          onClick={() => setConfirmId(null)}
                          disabled={cancelling === o.ID_Encomenda}
                          className="px-2 py-1 text-xs bg-white border border-[#E5E7EB] rounded text-[#6B7280]"
                        >
                          Não
                        </button>
                      </span>
                    ) : (
                      <button
                        data-testid={`downline-cancel-btn-${o.ID_Encomenda}`}
                        onClick={() => setConfirmId(o.ID_Encomenda)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#FCA5A5] text-[#991B1B] rounded hover:bg-[#FEF2F2]"
                      >
                        <X size={12} /> Cancelar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 p-3 bg-[#FFFBEB] border border-[#FCD34D] rounded-md text-xs text-[#92400E]" data-testid="downline-orders-disclaimer">
        <AlertTriangle size={12} className="inline mr-1" />
        Apenas encomendas em <strong>Aguardar Pagamento</strong> podem ser canceladas. Para encomendas já pagas, contacte o
        suporte para emitir Nota de Crédito.
      </div>
    </div>
  );
}
